import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import config from '../config';
import Notification from '../components/Notification';
import { CityContext } from '../contexts/CityContext';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import useNetworkStatus from '../hooks/useNetworkStatus';
import '../styles/register.css';

const Register = () => {
    const navigate = useNavigate();
    const { cities = [], isLoading: citiesLoading } = useContext(CityContext) || {};
    
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        mobile_number: '',
        city: '',
        password: '',
        confirmPassword: '',
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState({
        message: '',
        type: 'info',
        header: ''
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [socialAuth, setSocialAuth] = useState(null);
    const [socialEmail, setSocialEmail] = useState(null);
    const [isCheckingPhone, setIsCheckingPhone] = useState(false);
    const [phoneExists, setPhoneExists] = useState(false);
    const [isAppleAvailable, setIsAppleAvailable] = useState(false);
    const [isFacebookAvailable, setIsFacebookAvailable] = useState(false);
    const [isSocialLoginProcessing, setIsSocialLoginProcessing] = useState(false);
    const isOnline = useNetworkStatus();

    // Initialize Apple Sign-In (only on HTTPS - Apple doesn't support localhost)
    useEffect(() => {
        const isHttps = window.location.protocol === 'https:';
        
        if (window.AppleID && isHttps) {
            try {
                window.AppleID.auth.init({
                    clientId: 'com.wennze.web.service',
                    scope: 'name email',
                    redirectURI: window.location.origin,
                    usePopup: true
                });
                setIsAppleAvailable(true);
            } catch (error) {
                console.log('Apple Sign-In not available');
                setIsAppleAvailable(false);
            }
        } else if (!isHttps) {
            console.log('Apple Sign-In requires HTTPS - disabled on localhost');
            setIsAppleAvailable(false);
        }

        // Check Facebook SDK availability
        if (window.FB) {
            setIsFacebookAvailable(true);
        }
    }, []);

    const checkPhoneExists = async (phone) => {
        if (!validatePhone(phone)) {
            return false;
        }

        setIsCheckingPhone(true);
        try {
            const phoneNumber = `+243${phone}`;
            const response = await axios.post(`${config.backendUrl}/users/check-mobile-number/`, {
                mobile_number: phoneNumber
            });

            if (response.data.exists) {
                setErrors(prev => ({
                    ...prev,
                    mobile_number: 'Ce numéro est déjà utilisé'
                }));
                setPhoneExists(true);
                return true;
            } else {
                setPhoneExists(false);
                return false;
            }
        } catch (error) {
            console.error('Error checking phone:', error);
            return false;
        } finally {
            setIsCheckingPhone(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Clear errors as user types
        if (errors[name]) {
            setErrors(prev => {
                const { [name]: removed, ...rest } = prev;
                return rest;
            });
        }

        // Check phone number availability when it's valid
        if (name === 'mobile_number' && value.length === 9) {
            checkPhoneExists(value);
        } else if (name === 'mobile_number') {
            setPhoneExists(false);
        }
    };

    const validatePhone = (phone) => {
        const cleanNumber = phone.replace(/[\s-]/g, '');
        const phoneRegex = /^[0-9]{9}$/;
        return phoneRegex.test(cleanNumber);
    };

    const validatePassword = (password) => {
        const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{6,}$/;
        return passwordRegex.test(password);
    };

    const validateStep1 = () => {
        const newErrors = {};
        if (!formData.first_name.trim()) {
            newErrors.first_name = 'Prénom requis';
        }
        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Nom requis';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const newErrors = {};
        if (!formData.mobile_number) {
            newErrors.mobile_number = 'Numéro de téléphone requis';
        } else if (!validatePhone(formData.mobile_number)) {
            newErrors.mobile_number = 'Numéro invalide (9 chiffres)';
        }
        if (!formData.city) {
            newErrors.city = 'Ville requise';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep3 = () => {
        const newErrors = {};
        
        // Skip password validation for social auth
        if (!socialAuth) {
            if (!formData.password) {
                newErrors.password = 'Mot de passe requis';
            } else if (!validatePassword(formData.password)) {
                newErrors.password = 'Min 6 caractères avec lettres et chiffres';
            }
            if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
            }
        }
        
        if (!agreedToTerms) {
            newErrors.terms = 'Vous devez accepter les termes et conditions';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        let isValid = false;
        
        if (currentStep === 1) {
            isValid = validateStep1();
        } else if (currentStep === 2) {
            isValid = validateStep2();
        }
        
        if (isValid) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => prev - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateStep3()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const phoneNumber = `+243${formData.mobile_number}`;
            const username = `${formData.first_name}_${formData.last_name}_${formData.mobile_number}`.toLowerCase();

            const payload = {
                username,
                first_name: formData.first_name,
                last_name: formData.last_name,
                mobile_number: phoneNumber,
                city: formData.city,
                password: formData.password,
                read_terms: agreedToTerms,
                read_privacy: agreedToTerms,
            };

            if (socialEmail) {
                payload.email = socialEmail;
            }

            const response = await axios.post(`${config.backendUrl}/users/register/`, payload);

            if (response.data && response.data.user_id) {
                setNotification({
                    message: 'Inscription réussie! Vérifiez votre téléphone pour le code de vérification.',
                    type: 'success',
                    header: 'Succès'
                });

                setTimeout(() => {
                    navigate(`/verify-user/${response.data.user_id}`);
                }, 2000);
            } else {
                setNotification({
                    message: 'Erreur inattendue lors de l\'inscription',
                    type: 'error',
                    header: 'Erreur'
                });
            }
        } catch (error) {
            console.error('Registration error:', error);
            setNotification({
                message: error.response?.data?.error || 'Erreur lors de l\'inscription',
                type: 'error',
                header: 'Erreur'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFacebookSignup = () => {
        try {
            setIsSubmitting(true);
            setIsSocialLoginProcessing(true); // Show processing overlay
            window.FB.login((response) => {
                if (response.authResponse) {
                    const { accessToken, userID } = response.authResponse;
                    
                    // Get user profile
                    window.FB.api('/me', { fields: 'name,email,first_name,last_name' }, (userInfo) => {
                        // Send to backend
                        axios.post(
                            `${config.backendUrl}/users/facebook-login/`,
                            {
                                access_token: accessToken,
                                user_id: userID,
                                email: userInfo.email,
                                first_name: userInfo.first_name,
                                last_name: userInfo.last_name
                            },
                            { timeout: 30000 }
                        ).then((backendResponse) => {
                            if (backendResponse.data.token) {
                                // User already exists, log them in
                                Cookies.set('authToken', backendResponse.data.token);
                                localStorage.setItem('user', JSON.stringify(backendResponse.data.user));
                                navigate('/Dashboard');
                            } else if (backendResponse.data.requires_mobile) {
                                // New user, need to complete registration
                                navigate('/complete-profile', {
                                    state: {
                                        first_name: userInfo.first_name || '',
                                        last_name: userInfo.last_name || '',
                                        email: userInfo.email,
                                        socialAuth: 'facebook',
                                        socialToken: accessToken
                                    }
                                });
                            }
                        }).catch((error) => {
                            console.error('Facebook backend error:', error);
                            setNotification({
                                message: error.response?.data?.error || 'Erreur d\'inscription avec Facebook',
                                type: 'error',
                                header: 'Erreur'
                            });
                        }).finally(() => {
                            setIsSubmitting(false);
                            setIsSocialLoginProcessing(false); // Hide processing overlay
                        });
                    });
                } else {
                    setIsSubmitting(false);
                    setIsSocialLoginProcessing(false); // Hide processing overlay
                }
            }, { scope: 'email,public_profile' });
        } catch (error) {
            console.error('Facebook signup error:', error);
            setNotification({
                message: 'Erreur d\'inscription avec Facebook',
                type: 'error',
                header: 'Erreur'
            });
            setIsSubmitting(false);
            setIsSocialLoginProcessing(false); // Hide processing overlay
        }
    };

    const handleAppleSignup = async () => {
        if (!isOnline) {
            setNotification({
                message: 'Aucune connexion internet détectée',
                type: 'error',
                header: 'Erreur'
            });
            return;
        }

        try {
            setIsSubmitting(true);
            setIsSocialLoginProcessing(true); // Show processing overlay
            const response = await window.AppleID.auth.signIn();
            
            if (response.authorization) {
                const backendResponse = await axios.post(
                    `${config.backendUrl}/users/apple-login/`,
                    {
                        id_token: response.authorization.id_token,
                        code: response.authorization.code,
                        user: response.user
                    },
                    { timeout: 30000 }
                );

                if (backendResponse.data.token) {
                    // User already exists, log them in
                    Cookies.set('authToken', backendResponse.data.token);
                    localStorage.setItem('user', JSON.stringify(backendResponse.data.user));
                    navigate('/Dashboard');
                } else if (backendResponse.data.requires_mobile) {
                    // New user, need to complete registration
                    console.log('Apple requires_mobile - navigating to complete-profile with:', {
                        first_name: backendResponse.data.first_name,
                        last_name: backendResponse.data.last_name,
                        email: backendResponse.data.email,
                        socialAuth: 'apple',
                        socialToken: response.authorization.id_token
                    });
                    navigate('/complete-profile', {
                        state: {
                            first_name: backendResponse.data.first_name || '',
                            last_name: backendResponse.data.last_name || '',
                            email: backendResponse.data.email,
                            socialAuth: 'apple',
                            socialToken: response.authorization.id_token
                        }
                    });
                } else {
                    console.log('Apple response unexpected:', backendResponse.data);
                }
            }
        } catch (error) {
            console.error('Apple signup error:', error);
            setNotification({
                message: error.response?.data?.error || 'Erreur d\'inscription avec Apple',
                type: 'error',
                header: 'Erreur'
            });
        } finally {
            setIsSubmitting(false);
            setIsSocialLoginProcessing(false); // Hide processing overlay
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            setIsSocialLoginProcessing(true); // Show processing overlay
            const decoded = jwtDecode(credentialResponse.credential);
            
            // Redirect to CompleteProfile with Google data
            navigate('/complete-profile', {
                state: {
                    first_name: decoded.given_name || '',
                    last_name: decoded.family_name || '',
                    email: decoded.email,
                    socialAuth: 'google',
                    socialToken: credentialResponse.credential
                }
            });
        } catch (error) {
            console.error('Google signup error:', error);
            setNotification({
                message: 'Erreur lors de la connexion avec Google',
                type: 'error',
                header: 'Erreur'
            });
        } finally {
            setIsSocialLoginProcessing(false); // Hide processing overlay
        }
    };

    return (
        <>
        <div className="modern-auth-page">
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={() => setNotification({ message: '', type: 'info', header: '' })}
                />
            )}

            <div className="register-card" style={{ maxWidth: '500px', background: 'white', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', padding: '40px' }}>
                <h2 className="register-title">Créer un compte</h2>
                
                {/* Social Signup - Only show on Step 1 */}
                {currentStep === 1 && !socialAuth && (
                    <div className="social-signup-section" style={{ 
                        marginBottom: '30px', 
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                    }}>
                        <p style={{ 
                            marginBottom: '20px', 
                            color: '#495057',
                            fontSize: '15px',
                            fontWeight: '500',
                            textAlign: 'center'
                        }}>
                            Inscription rapide avec:
                        </p>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <div className="google-button-wrapper" style={{ flex: '1', minWidth: '150px', maxWidth: '200px' }}>
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => {
                                        setNotification({
                                            message: "Échec de l'inscription avec Google",
                                            type: 'error',
                                            header: 'Erreur'
                                        });
                                    }}
                                    theme="outline"
                                    size="large"
                                    text="signup_with"
                                    locale="fr"
                                />
                            </div>
                            
                            {isAppleAvailable && (
                                <button
                                    type="button"
                                    onClick={handleAppleSignup}
                                    disabled={isSubmitting}
                                    style={{
                                    flex: '1',
                                    minWidth: '150px',
                                    maxWidth: '200px',
                                    height: '44px',
                                    backgroundColor: '#000',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    transition: 'background-color 0.2s',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#333'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#000'}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                                </svg>
                                Apple
                            </button>
                            )}
                            
                            {isFacebookAvailable && (
                            <button
                                type="button"
                                onClick={handleFacebookSignup}
                                disabled={isSubmitting}
                                style={{
                                    flex: '1',
                                    minWidth: '150px',
                                    maxWidth: '200px',
                                    height: '44px',
                                    backgroundColor: '#1877f2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    transition: 'background-color 0.2s',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#166fe5'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#1877f2'}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                                Facebook
                            </button>
                            )}
                        </div>
                        
                        <div style={{ 
                            margin: '24px 0 0', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '12px' 
                        }}>
                            <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }}></div>
                            <span style={{ color: '#6c757d', fontSize: '13px', fontWeight: '500' }}>OU</span>
                            <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }}></div>
                        </div>
                    </div>
                )}

                {/* Progress Indicator */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
                    {[1, 2, 3].map(step => {
                        const getStepColor = (stepNum) => {
                            if (stepNum === 1) return '#c51618'; // Red/Yellow (brand color 1)
                            if (stepNum === 2) return '#FFC107'; // Yellow (brand color 2)
                            if (stepNum === 3) return '#003366'; // Dark blue
                            return '#e0e0e0';
                        };
                        return (
                            <div key={step} style={{ flex: 1, marginRight: step < 3 ? '10px' : '0' }}>
                                <div style={{
                                    height: '4px',
                                    backgroundColor: step <= currentStep ? getStepColor(step) : '#e0e0e0',
                                    borderRadius: '2px',
                                    transition: 'background-color 0.3s'
                                }} />
                                <p style={{
                                    textAlign: 'center',
                                    fontSize: '12px',
                                    marginTop: '5px',
                                    color: step <= currentStep ? getStepColor(step) : '#999'
                                }}>
                                    Étape {step}
                                </p>
                            </div>
                        );
                    })}
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Step 1: Names */}
                    {currentStep === 1 && (
                        <div>
                            <h3 style={{ marginBottom: '20px', color: '#333' }}>Informations personnelles</h3>
                            
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Prénom *</label>
                                <input
                                    type="text"
                                    name="first_name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    placeholder="Votre prénom"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: errors.first_name ? '1px solid red' : '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                    disabled={socialAuth}
                                />
                                {errors.first_name && <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.first_name}</p>}
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Nom *</label>
                                <input
                                    type="text"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    placeholder="Votre nom"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: errors.last_name ? '1px solid red' : '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                    disabled={socialAuth}
                                />
                                {errors.last_name && <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.last_name}</p>}
                            </div>

                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={!formData.first_name.trim() || !formData.last_name.trim()}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    backgroundColor: (!formData.first_name.trim() || !formData.last_name.trim()) ? '#ccc' : '#003366',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    cursor: (!formData.first_name.trim() || !formData.last_name.trim()) ? 'not-allowed' : 'pointer',
                                    marginTop: '10px',
                                    opacity: (!formData.first_name.trim() || !formData.last_name.trim()) ? 0.6 : 1
                                }}
                            >
                                Suivant
                            </button>
                        </div>
                    )}

                    {/* Step 2: Contact & Location */}
                    {currentStep === 2 && (
                        <div>
                            <h3 style={{ marginBottom: '20px', color: '#333' }}>Contact et localisation</h3>
                            
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Numéro de téléphone *</label>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ padding: '10px', backgroundColor: '#f0f0f0', border: '1px solid #ddd', borderRight: 'none', borderRadius: '4px 0 0 4px' }}>+243</span>
                                    <input
                                        type="text"
                                        name="mobile_number"
                                        value={formData.mobile_number}
                                        onChange={handleChange}
                                        placeholder="999999999"
                                        maxLength={9}
                                        style={{
                                            flex: 1,
                                            padding: '10px',
                                            border: errors.mobile_number ? '1px solid red' : '1px solid #ddd',
                                            borderRadius: '0 4px 4px 0',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                {errors.mobile_number && <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.mobile_number}</p>}
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Ville *</label>
                                <select
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: errors.city ? '1px solid red' : '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">Sélectionnez une ville</option>
                                    {cities.map(city => (
                                        <option key={city.id} value={city.id}>{city.name}</option>
                                    ))}
                                </select>
                                {errors.city && <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.city}</p>}
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Retour
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    disabled={!formData.mobile_number || !formData.city || !validatePhone(formData.mobile_number) || phoneExists || isCheckingPhone}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        backgroundColor: (!formData.mobile_number || !formData.city || !validatePhone(formData.mobile_number) || phoneExists || isCheckingPhone) ? '#ccc' : '#003366',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        cursor: (!formData.mobile_number || !formData.city || !validatePhone(formData.mobile_number) || phoneExists || isCheckingPhone) ? 'not-allowed' : 'pointer',
                                        opacity: (!formData.mobile_number || !formData.city || !validatePhone(formData.mobile_number) || phoneExists || isCheckingPhone) ? 0.6 : 1
                                    }}
                                >
                                    {isCheckingPhone ? 'Vérification...' : 'Suivant'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Password & Terms */}
                    {currentStep === 3 && (
                        <div>
                            <h3 style={{ marginBottom: '20px', color: '#333' }}>Sécurité et confirmation</h3>
                            
                            {!socialAuth && (
                                <>
                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Mot de passe *</label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder="Min 6 caractères"
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    paddingRight: '40px',
                                                    border: errors.password ? '1px solid red' : '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '10px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '18px'
                                                }}
                                            >
                                                {showPassword ? '👁️' : '👁️‍🗨️'}
                                            </button>
                                        </div>
                                        {errors.password && <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.password}</p>}
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Confirmer le mot de passe *</label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                name="confirmPassword"
                                                value={formData.confirmPassword}
                                                onChange={handleChange}
                                                placeholder="Confirmez votre mot de passe"
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    paddingRight: '40px',
                                                    border: errors.confirmPassword ? '1px solid red' : '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '10px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '18px'
                                                }}
                                            >
                                                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                                            </button>
                                        </div>
                                        {errors.confirmPassword && <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.confirmPassword}</p>}
                                    </div>
                                </>
                            )}

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'flex', alignItems: 'start', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={agreedToTerms}
                                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                                        style={{ marginRight: '10px', marginTop: '4px' }}
                                    />
                                    <span style={{ fontSize: '14px' }}>
                                        J'accepte les <a href="/terms-conditions/conditions-utilisation" target="_blank" style={{ color: '#003366' }}>termes et conditions</a> et la <a href="/terms-conditions/politique-confidentialite" target="_blank" style={{ color: '#003366' }}>politique de confidentialité</a>
                                    </span>
                                </label>
                                {errors.terms && <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.terms}</p>}
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        cursor: 'pointer'
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Retour
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        opacity: isSubmitting ? 0.7 : 1
                                    }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Inscription...' : 'S\'inscrire'}
                                </button>
                            </div>
                        </div>
                    )}
                </form>

                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <p style={{ fontSize: '14px', color: '#666' }}>
                        Vous avez déjà un compte? <a href="/login" style={{ color: '#003366', textDecoration: 'none' }}>Connexion</a>
                    </p>
                </div>
            </div>
            
            {/* Social Login Processing Overlay */}
            {isSocialLoginProcessing && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000,
                    flexDirection: 'column'
                }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        border: '4px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '4px solid #fff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{
                        color: '#fff',
                        marginTop: '16px',
                        fontSize: '16px',
                        textAlign: 'center'
                    }}>Authentification en cours...</p>
                </div>
            )}
        </div>
        
        <style jsx>{`
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
        </>
    );
};

export default Register;
